import {
  Button,
  Callout,
  Card,
  CardHeader,
  Field,
  Input,
} from '@provenance/design';
import type { ChamberSubmitInfo } from '@provenance/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicApiError, publicRequest } from '../api';

/**
 * A chamber's own submission page — spec v4 §25, reached at `/{chamberSlug}/submit`.
 *
 * Two jobs: publish the chamber's blank templates, and start the magic-link flow.
 *
 * **Nothing here discloses who a chamber's exporters are.** The response to a link request
 * is identical whether or not the address is registered, and the copy says so plainly
 * rather than implying a link is on its way when it is not.
 */
export function Submit() {
  const { chamberSlug = '' } = useParams();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery({
    queryKey: ['submit-info', chamberSlug],
    queryFn: () =>
      publicRequest<ChamberSubmitInfo>(`/api/v1/public/chambers/${chamberSlug}/submit-info`),
    retry: false,
  });

  // Per-chamber theming, applied as custom properties at runtime — the same mechanism
  // the registration page uses. Tokens only: a hard-coded colour here would silently
  // break white-labelling for every chamber but the default one.
  useEffect(() => {
    const root = document.documentElement;
    if (info.data) {
      root.style.setProperty('--pv-brand', info.data.primaryColor);
      root.style.setProperty('--pv-accent', info.data.secondaryColor);
    }
    return () => {
      root.style.removeProperty('--pv-brand');
      root.style.removeProperty('--pv-accent');
    };
  }, [info.data]);

  async function requestLink(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      await publicRequest('/api/v1/public/upload/request-link', {
        method: 'POST',
        body: { chamberSlug, email },
      });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof PublicApiError ? err.message : 'We could not send the link. Try again.',
      );
    } finally {
      setSending(false);
    }
  }

  if (info.isError) {
    // Three different situations, which used to share one message: a mistyped address, a
    // chamber that exists but has not gone live, and the service being unreachable.
    const err = info.error instanceof PublicApiError ? info.error : null;
    const notLive = err?.code === 'chamber_not_ready';
    const notFound = err?.status === 404;
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Callout
          tone={notLive ? 'warn' : 'stop'}
          icon={<AlertTriangle className="size-4" />}
          title={notLive ? 'Not open yet' : notFound ? 'No such chamber' : 'Something went wrong'}
        >
          {notLive
            ? err!.message
            : notFound
              ? `We could not find a chamber called “${chamberSlug ?? ''}”. Check the link your chamber gave you.`
              : 'We could not load this page. Check your connection and try again.'}
        </Callout>
      </div>
    );
  }

  const types = info.data?.documentTypes ?? [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
      <header className="mb-8">
        <p className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
          {info.data?.chamberName ?? 'Loading…'}
        </p>
        <h1 className="mt-1 [font-size:var(--pv-text-xl)] font-semibold">
          Submit a certificate of origin
        </h1>
        <p className="mt-2 max-w-prose [font-size:14px] text-[var(--pv-muted)]">
          Download the form for the certificate you need, fill it in and sign it, then upload
          it here. Only people your company has registered as signatories can upload.
        </p>
      </header>

      {/* Templates first: an exporter arriving here usually needs the form before
          anything else, and making them authenticate to reach a blank PDF would be
          friction for no security — the forms are public documents. */}
      <Card className="mb-6">
        <CardHeader
          title="The forms this chamber issues"
          description="Use the current form. An out-of-date one will be sent back."
        />
        <div className="flex flex-col divide-y divide-[var(--pv-line)]">
          {info.isLoading && (
            <p className="px-4 py-6 [font-size:13px] text-[var(--pv-muted)]">Loading…</p>
          )}
          {!info.isLoading && types.length === 0 && (
            <p className="px-4 py-6 [font-size:13px] text-[var(--pv-muted)]">
              This chamber is not yet set up to issue certificates online. Contact them
              directly.
            </p>
          )}
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="[font-size:13.5px] font-medium">{t.name}</p>
                <p className="mt-0.5 [font-size:12.5px] text-[var(--pv-muted)]">
                  {t.agreement ?? 'Non-preferential'}
                </p>
              </div>
              {t.hasTemplate ? (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Download className="size-3.5" />}
                  onClick={async () => {
                    const { url } = await publicRequest<{ url: string }>(
                      `/api/v1/public/chambers/${chamberSlug}/templates/${t.id}`,
                    );
                    window.open(url, '_blank', 'noopener');
                  }}
                >
                  Download form
                  <span className="sr-only"> for {t.name}</span>
                </Button>
              ) : (
                <span className="[font-size:12.5px] text-[var(--pv-faint)]">Form from your chamber</span>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Upload a completed certificate"
          description="We will email you a link. It is valid for 15 minutes and can be used once."
        />
        <div className="p-4">
          {sent ? (
            <Callout
              tone="ok"
              icon={<CheckCircle2 className="size-4" />}
              title="Check your email"
            >
              {/* Deliberately non-committal. Confirming that the address is registered
                  would turn this form into a way to enumerate the chamber's exporters. */}
              If that address is registered with this chamber as a signatory, we have sent it
              a link to upload a certificate.
            </Callout>
          ) : (
            <form onSubmit={requestLink} className="flex flex-col gap-3" noValidate>
              <Field
                label="Your email address"
                required
                help="The address your company registered you under as a signatory."
                error={error ?? undefined}
              >
                <Input
                  type="email"
                  value={email}
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com.au"
                />
              </Field>
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  loading={sending}
                  disabled={!email.trim()}
                  leadingIcon={<Mail className="size-3.5" />}
                >
                  Email me a link
                </Button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}
